<template>
  <transition name="appointment-home-reveal">
    <section v-if="hasContent" class="appointment-home" :style="themeStyle" aria-labelledby="appointment-home-title">
      <header class="appointment-home__header">
        <span class="appointment-home__heading-icon" aria-hidden="true">
          <v-icon>mdi-calendar-account-outline</v-icon>
        </span>
        <div class="appointment-home__heading-copy">
          <span>DEINE TRAINERTERMINE</span>
          <h2 id="appointment-home-title">Nächste Termine & Feedback</h2>
        </div>
        <button type="button" class="appointment-home__all" @click="openAppointments">
          Alle ansehen
          <v-icon small>mdi-chevron-right</v-icon>
        </button>
      </header>

      <div
        class="appointment-home__grid"
        :class="{
          'appointment-home__grid--single': !visibleUpcomingAppointments.length || !pendingRatingAppointments.length,
        }">
        <article v-if="visibleUpcomingAppointments.length" class="appointment-panel appointment-panel--upcoming">
          <div class="appointment-panel__label">
            <span><v-icon small>mdi-clock-outline</v-icon> Als Nächstes</span>
            <small>{{ upcomingCountLabel }}</small>
          </div>

          <div class="appointment-list">
            <button
              v-for="appointment in visibleUpcomingAppointments"
              :key="appointment._id"
              type="button"
              class="appointment-row"
              @click="openAppointments">
              <span class="appointment-date" aria-hidden="true">
                <strong>{{ appointmentDay(appointment) }}</strong>
                <small>{{ appointmentMonth(appointment) }}</small>
              </span>
              <span class="appointment-row__copy">
                <small>{{ relativeAppointmentDate(appointment) }}</small>
                <strong>{{ appointment.title || "Trainertermin" }}</strong>
                <span>{{ appointmentTime(appointment) }}</span>
                <span class="appointment-row__meta">
                  <span><v-icon x-small>mdi-account-outline</v-icon>{{ trainerName(appointment) }}</span>
                  <span v-if="appointmentLocation(appointment)">
                    <v-icon x-small>mdi-map-marker-outline</v-icon>{{ appointmentLocation(appointment) }}
                  </span>
                </span>
              </span>
              <v-icon class="appointment-row__chevron" small>mdi-chevron-right</v-icon>
            </button>
          </div>

          <button v-if="additionalUpcomingCount" type="button" class="appointment-panel__more" @click="openAppointments">
            + {{ additionalUpcomingCount }} {{ additionalUpcomingCount === 1 ? "weiterer Termin" : "weitere Termine" }}
          </button>
        </article>

        <button v-if="primaryPendingRating" type="button" class="appointment-feedback" @click="openRating(primaryPendingRating)">
          <span class="appointment-feedback__glow" aria-hidden="true"></span>
          <span class="appointment-feedback__topline">
            <span class="appointment-feedback__icon"><v-icon>mdi-star-outline</v-icon></span>
            <span class="appointment-feedback__badge"> {{ pendingRatingAppointments.length }} offen </span>
          </span>
          <span class="appointment-feedback__copy">
            <small>DEIN FEEDBACK</small>
            <strong>Wie war dein Termin?</strong>
            <span> {{ primaryPendingRating.title || "Trainertermin" }} · {{ formatAppointmentDate(primaryPendingRating) }} </span>
          </span>
          <span class="appointment-feedback__action">
            Jetzt bewerten
            <v-icon small>mdi-arrow-right</v-icon>
          </span>
        </button>
      </div>
    </section>
  </transition>
</template>

<script>
import api from "@/api"

const INACTIVE_APPOINTMENT_STATUSES = new Set(["cancelled", "canceled", "abgesagt", "noshow", "no_show"])

export default {
  name: "CustomerAppointmentHome",
  data() {
    return {
      loading: false,
      upcomingAppointments: [],
      pastAppointments: [],
      hasBeenActivated: false,
    }
  },
  computed: {
    activeUpcomingAppointments() {
      return this.upcomingAppointments.filter((appointment) => !INACTIVE_APPOINTMENT_STATUSES.has(String(appointment.status || "").toLowerCase()))
    },
    visibleUpcomingAppointments() {
      return this.activeUpcomingAppointments.slice(0, 2)
    },
    additionalUpcomingCount() {
      return Math.max(0, this.activeUpcomingAppointments.length - this.visibleUpcomingAppointments.length)
    },
    upcomingCountLabel() {
      const count = this.activeUpcomingAppointments.length
      return `${count} ${count === 1 ? "Termin" : "Termine"}`
    },
    pendingRatingAppointments() {
      return this.pastAppointments.filter((appointment) => appointment.canRate && !appointment.rating)
    },
    primaryPendingRating() {
      return this.pendingRatingAppointments[0] || null
    },
    hasContent() {
      return Boolean(this.activeUpcomingAppointments.length || this.pendingRatingAppointments.length)
    },
    primaryColor() {
      const theme = this.$vuetify && this.$vuetify.theme
      const themes = theme && theme.themes
      const currentTheme = themes && (theme.dark ? themes.dark : themes.light)
      const primary = (currentTheme && currentTheme.primary) || (themes && themes.light && themes.light.primary)
      return typeof primary === "string" && primary.trim() ? primary.trim() : "#52ae32"
    },
    primaryRgb() {
      const value = this.primaryColor.replace("#", "")
      const normalized =
        value.length === 3
          ? value
              .split("")
              .map((part) => part + part)
              .join("")
          : value.slice(0, 6)
      if (!/^[0-9a-f]{6}$/i.test(normalized)) return "82, 174, 50"
      return [0, 2, 4].map((index) => parseInt(normalized.slice(index, index + 2), 16)).join(", ")
    },
    themeStyle() {
      return {
        "--appointment-primary": this.primaryColor,
        "--appointment-primary-rgb": this.primaryRgb,
      }
    },
  },
  created() {
    this.loadAppointments()
  },
  activated() {
    if (this.hasBeenActivated) this.loadAppointments()
    this.hasBeenActivated = true
  },
  methods: {
    async loadAppointments() {
      if (this.loading) return
      this.loading = true
      try {
        const response = await api.reservationTool.getMyCalendarAppointments()
        const data = response && response.data ? response.data : {}
        this.upcomingAppointments = Array.isArray(data.upcoming) ? data.upcoming : []
        this.pastAppointments = Array.isArray(data.past) ? data.past : []
      } catch (error) {
        // Die Startseite bleibt bei einem temporären API-Fehler bewusst ruhig.
        console.error("Kundentermine auf der Startseite konnten nicht geladen werden:", error)
      } finally {
        this.loading = false
      }
    },
    appointmentMoment(appointment) {
      if (!appointment || !appointment.date) return null
      const parsed = this.$moment(`${appointment.date} ${appointment.start || "00:00"}`, "YYYY-MM-DD HH:mm", true)
      return parsed.isValid() ? parsed : null
    },
    appointmentDay(appointment) {
      const date = this.appointmentMoment(appointment)
      return date ? date.format("DD") : "--"
    },
    appointmentMonth(appointment) {
      const date = this.appointmentMoment(appointment)
      return date ? date.format("MMM").replace(".", "").toUpperCase() : "---"
    },
    relativeAppointmentDate(appointment) {
      const date = this.appointmentMoment(appointment)
      if (!date) return "Demnächst"
      const difference = date.clone().startOf("day").diff(this.$moment().startOf("day"), "days")
      if (difference === 0) return "Heute"
      if (difference === 1) return "Morgen"
      return date.format("dddd")
    },
    formatAppointmentDate(appointment) {
      const date = this.appointmentMoment(appointment)
      return date ? date.format("DD.MM.YYYY") : appointment.date || ""
    },
    appointmentTime(appointment) {
      if (!appointment || !appointment.start) return "Zeit wird noch festgelegt"
      return appointment.end ? `${appointment.start} – ${appointment.end} Uhr` : `${appointment.start} Uhr`
    },
    trainerName(appointment) {
      const trainer = appointment && appointment.trainer
      if (!trainer) return "Trainerteam"
      return `${trainer.firstName || ""} ${trainer.lastName || ""}`.trim() || "Trainerteam"
    },
    appointmentLocation(appointment) {
      if (appointment && appointment.specialType === "online_meeting") return "Microsoft Teams"
      return appointment && appointment.studio ? appointment.studio.name || "" : ""
    },
    openAppointments() {
      const navigation = this.$router.push("/reservation")
      if (navigation && typeof navigation.catch === "function") navigation.catch(() => {})
    },
    openRating(appointment) {
      const navigation = this.$router.push({
        path: "/reservation",
        query: { rate: String(appointment._id) },
      })
      if (navigation && typeof navigation.catch === "function") navigation.catch(() => {})
    },
  },
}
</script>

<style scoped>
.appointment-home {
  --appointment-primary: #52ae32;
  --appointment-primary-rgb: 82, 174, 50;
  padding: 16px;
  margin-bottom: 14px;
  overflow: hidden;
  color: #fff;
  background: radial-gradient(circle at 12% 0%, rgba(var(--appointment-primary-rgb), 0.16), transparent 34%),
    linear-gradient(145deg, rgba(35, 37, 38, 0.98), rgba(24, 25, 26, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.22);
}

.appointment-home button {
  font-family: inherit;
}

.appointment-home__header {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 13px;
}

.appointment-home__heading-icon {
  display: grid;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  place-items: center;
  background: rgba(var(--appointment-primary-rgb), 0.15);
  border: 1px solid rgba(var(--appointment-primary-rgb), 0.34);
  border-radius: 13px;
}

.appointment-home__heading-icon .v-icon {
  color: var(--appointment-primary);
}

.appointment-home__heading-copy {
  flex: 1 1 auto;
  min-width: 0;
}

.appointment-home__heading-copy > span,
.appointment-feedback__copy > small {
  display: block;
  color: var(--appointment-primary);
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.13em;
}

.appointment-home__heading-copy h2 {
  margin: 2px 0 0;
  color: #fff;
  font-size: 1.125rem;
  font-weight: 900;
  line-height: 1.2;
}

.appointment-home__all,
.appointment-panel__more {
  color: rgba(255, 255, 255, 0.72);
  background: transparent;
  border: 0;
  font-size: 0.875rem;
  font-weight: 800;
  cursor: pointer;
}

.appointment-home__all {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 2px;
  padding: 8px 4px 8px 10px;
}

.appointment-home__all .v-icon {
  color: currentColor;
}

.appointment-home__grid {
  display: grid;
  grid-template-columns: minmax(0, 1.65fr) minmax(240px, 0.85fr);
  gap: 11px;
}

.appointment-home__grid--single {
  grid-template-columns: 1fr;
}

.appointment-panel,
.appointment-feedback {
  min-width: 0;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 15px;
}

.appointment-panel {
  padding: 11px;
}

.appointment-panel__label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2px 8px;
}

.appointment-panel__label > span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.875rem;
  font-weight: 900;
}

.appointment-panel__label .v-icon {
  color: var(--appointment-primary);
}

.appointment-panel__label small {
  color: rgba(255, 255, 255, 0.46);
  font-size: 0.75rem;
  font-weight: 700;
}

.appointment-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.appointment-row {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 10px;
  padding: 9px;
  color: #fff;
  text-align: left;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.055);
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}

.appointment-row:hover {
  background: rgba(var(--appointment-primary-rgb), 0.09);
  border-color: rgba(var(--appointment-primary-rgb), 0.3);
  transform: translateY(-1px);
}

.appointment-date {
  display: grid;
  flex: 0 0 47px;
  width: 47px;
  height: 54px;
  align-content: center;
  justify-items: center;
  background: rgba(var(--appointment-primary-rgb), 0.13);
  border: 1px solid rgba(var(--appointment-primary-rgb), 0.24);
  border-radius: 11px;
}

.appointment-date strong {
  font-size: 1.375rem;
  font-weight: 900;
  line-height: 1;
}

.appointment-date small {
  margin-top: 4px;
  color: var(--appointment-primary);
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.06em;
}

.appointment-row__copy {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.appointment-row__copy > small {
  color: var(--appointment-primary);
  font-size: 0.75rem;
  font-weight: 900;
}

.appointment-row__copy > strong {
  margin-top: 1px;
  font-size: 1rem;
  font-weight: 900;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.appointment-row__copy > span:not(.appointment-row__meta) {
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.67);
  font-size: 0.875rem;
  line-height: 1.4;
}

.appointment-row__meta {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 6px;
  color: rgba(255, 255, 255, 0.46);
  font-size: 0.875rem;
  line-height: 1.4;
}

.appointment-row__meta > span {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 4px;
  overflow-wrap: anywhere;
}

.appointment-row__meta .v-icon,
.appointment-row__chevron {
  color: rgba(255, 255, 255, 0.38);
}

.appointment-panel__more {
  display: block;
  padding: 9px 2px 1px;
  color: var(--appointment-primary);
}

.appointment-feedback {
  position: relative;
  display: flex;
  min-height: 125px;
  flex-direction: column;
  padding: 13px;
  overflow: hidden;
  color: #fff;
  text-align: left;
  background: linear-gradient(145deg, rgba(var(--appointment-primary-rgb), 0.16), rgba(var(--appointment-primary-rgb), 0.055)), rgba(0, 0, 0, 0.18);
  border-color: rgba(var(--appointment-primary-rgb), 0.28);
  cursor: pointer;
}

.appointment-feedback__glow {
  position: absolute;
  top: -50px;
  right: -42px;
  width: 135px;
  height: 135px;
  background: rgba(var(--appointment-primary-rgb), 0.18);
  border-radius: 50%;
  filter: blur(12px);
}

.appointment-feedback__topline,
.appointment-feedback__action {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.appointment-feedback__icon {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 11px;
}

.appointment-feedback__icon .v-icon {
  color: #f5bf42;
}

.appointment-feedback__badge {
  padding: 4px 8px;
  color: #fff;
  background: rgba(var(--appointment-primary-rgb), 0.25);
  border: 1px solid rgba(var(--appointment-primary-rgb), 0.35);
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 900;
  text-transform: uppercase;
}

.appointment-feedback__copy {
  position: relative;
  z-index: 1;
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: center;
  padding: 9px 0;
}

.appointment-feedback__copy > strong {
  margin-top: 2px;
  font-size: 1rem;
  font-weight: 900;
}

.appointment-feedback__copy > span {
  margin-top: 3px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.875rem;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.appointment-feedback__action {
  justify-content: flex-start;
  gap: 5px;
  color: var(--appointment-primary);
  font-size: 0.875rem;
  font-weight: 900;
}

.appointment-feedback__action .v-icon {
  color: currentColor;
  transition: transform 0.2s ease;
}

.appointment-feedback:hover .appointment-feedback__action .v-icon {
  transform: translateX(3px);
}

.appointment-row:focus-visible,
.appointment-feedback:focus-visible,
.appointment-home__all:focus-visible,
.appointment-panel__more:focus-visible {
  outline: 2px solid var(--appointment-primary);
  outline-offset: 2px;
}

.appointment-home-reveal-enter-active,
.appointment-home-reveal-leave-active {
  transition: opacity 0.24s ease, transform 0.24s ease;
}

.appointment-home-reveal-enter,
.appointment-home-reveal-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

@media (max-width: 980px) {
  .appointment-home__grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .appointment-home {
    padding: 13px;
    border-radius: 17px;
  }

  .appointment-list {
    grid-template-columns: 1fr;
  }

  .appointment-feedback {
    min-height: 118px;
  }
}

@media (max-width: 430px) {
  .appointment-home__heading-copy h2 {
    font-size: 1rem;
  }
}
</style>
